import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export interface User {
  _id: string;
  name?: string;
  email?: string;
  status?: "active" | "inactive";
  approved?: boolean;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
}

export type ApproveUserPayload = {
  approved?: boolean;
};

export type ApproveUserResponse = {
  success: boolean;
  message: string;
  data: User;
};

const approveUser = async (
  id: string,
  payload?: ApproveUserPayload
): Promise<ApproveUserResponse> => {
  const { data } = await customAxios.patch<ApproveUserResponse>(
    `/users/approve/${id}`
  );
  return data;
};

export const useApproveUser = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ApproveUserResponse,
    AxiosError,
    { id: string; payload?: ApproveUserPayload }
  >(({ id, payload }) => approveUser(id, payload), {
    onSuccess: async (data, variables) => {
      showNotification({
        title: "Success",
        message: data.message ?? "User approved",
        color: "green",
      });

      await queryClient.invalidateQueries(["users"]);
      await queryClient.invalidateQueries(["user", variables.id]);
    },
    onError: (error) =>
      showNotification({
        title: "Error",
        message:
          (error.response?.data as { message?: string })?.message ??
          "Failed to approve user",
        color: "red",
      }),
  });
};
